import React, { useState, useEffect } from 'react'
import { Plus } from 'lucide-react'
import { Card, Button, Modal } from '../ui'
import {
  type Organization,
  organizationService,
  type OrganizationFeature,
  type Feature
} from '../../services/organizationService'
import { useToast } from '../../contexts/ToastContext'

interface OrganizationFeaturesProps {
  organization: Organization
  onRefresh?: () => void
}

const OrganizationFeatures: React.FC<OrganizationFeaturesProps> = ({
  organization
}) => {
  const { showError, showSuccess } = useToast()
  const [features, setFeatures] = useState<OrganizationFeature[]>([])
  const [availableFeatures, setAvailableFeatures] = useState<Feature[]>([])
  const [loading, setLoading] = useState(true)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(
    null
  )
  const [savingFeatureId, setSavingFeatureId] = useState<string | null>(null)
  const [showReasonModal, setShowReasonModal] = useState(false)
  const [featureToToggle, setFeatureToToggle] = useState<{
    featureId: string
    currentStatus: boolean
  } | null>(null)
  const [changeReason, setChangeReason] = useState('')

  // Load organization features and available features
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)

        const [orgFeaturesResponse, availableFeaturesResponse] =
          await Promise.all([
            organizationService.getOrganizationFeatures(organization.id),
            organizationService.getAvailableFeatures()
          ])

        if (orgFeaturesResponse.success) {
          setFeatures(orgFeaturesResponse.data)
        }

        if (availableFeaturesResponse.success) {
          setAvailableFeatures(availableFeaturesResponse.data)
        }
      } catch (error) {
        console.error('Error loading features:', error)
        showError(
          'Failed to load features',
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred'
        )
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [organization.id, showError])

  const getAvailableFeaturesToAdd = () => {
    const existingFeatureIds = features.map((f) => f.featureId || f.id)
    return availableFeatures.filter((af) => !existingFeatureIds.includes(af.id))
  }

  const handleAddFeatureClick = () => {
    setIsAddingNew(true)
    setSelectedFeatureId(null)
  }

  const handleCancelAdd = () => {
    setIsAddingNew(false)
    setSelectedFeatureId(null)
  }

  const handleAddFeature = async () => {
    if (!selectedFeatureId) {
      showError(
        'Please select a feature',
        'You must select a feature from the dropdown'
      )
      return
    }

    try {
      setSavingFeatureId(selectedFeatureId)

      // Close the add form immediately and show loading state
      const featureIdToAdd = selectedFeatureId
      setIsAddingNew(false)
      setSelectedFeatureId(null)

      // Add feature using POST endpoint
      const response = await organizationService.addOrganizationFeature(
        organization.id,
        featureIdToAdd,
        true,
        'Added by admin'
      )

      if (response.success) {
        showSuccess('Feature added successfully')
        // Refresh features list to show updated data
        const orgFeaturesResponse =
          await organizationService.getOrganizationFeatures(organization.id)
        if (orgFeaturesResponse.success) {
          setFeatures(orgFeaturesResponse.data)
        }
      } else {
        throw new Error('Failed to add feature')
      }
    } catch (error) {
      console.error('Error adding feature:', error)
      showError(
        'Failed to add feature',
        error instanceof Error ? error.message : 'An unexpected error occurred'
      )
      // Refresh features list even on error to ensure UI is in sync
      try {
        const orgFeaturesResponse =
          await organizationService.getOrganizationFeatures(organization.id)
        if (orgFeaturesResponse.success) {
          setFeatures(orgFeaturesResponse.data)
        }
      } catch (refreshError) {
        console.error('Error refreshing features:', refreshError)
      }
    } finally {
      setSavingFeatureId(null)
    }
  }

  const handleToggleFeatureClick = (
    featureId: string,
    currentStatus: boolean
  ) => {
    setFeatureToToggle({ featureId, currentStatus })
    setShowReasonModal(true)
    setChangeReason('')
  }

  const handleCancelReasonModal = () => {
    setShowReasonModal(false)
    setFeatureToToggle(null)
    setChangeReason('')
  }

  const handleConfirmToggle = async () => {
    if (!featureToToggle) return

    if (!changeReason.trim()) {
      showError('Reason required', 'Please provide a reason for this change')
      return
    }

    try {
      setSavingFeatureId(featureToToggle.featureId)

      // Close modal immediately and show loader on the feature
      setShowReasonModal(false)
      setFeatureToToggle(null)
      setChangeReason('')

      const response = await organizationService.updateOrganizationFeature(
        organization.id,
        featureToToggle.featureId,
        !featureToToggle.currentStatus,
        changeReason.trim()
      )

      if (response.success) {
        showSuccess(
          `Feature ${
            !featureToToggle.currentStatus ? 'enabled' : 'disabled'
          } successfully`
        )
        // Refresh features list to show updated data
        const orgFeaturesResponse =
          await organizationService.getOrganizationFeatures(organization.id)
        if (orgFeaturesResponse.success) {
          setFeatures(orgFeaturesResponse.data)
        }
      } else {
        throw new Error('Failed to toggle feature')
      }
    } catch (error) {
      console.error('Error toggling feature:', error)
      showError(
        'Failed to toggle feature',
        error instanceof Error ? error.message : 'An unexpected error occurred'
      )
      // Refresh features list even on error to ensure UI is in sync
      try {
        const orgFeaturesResponse =
          await organizationService.getOrganizationFeatures(organization.id)
        if (orgFeaturesResponse.success) {
          setFeatures(orgFeaturesResponse.data)
        }
      } catch (refreshError) {
        console.error('Error refreshing features:', refreshError)
      }
    } finally {
      setSavingFeatureId(null)
    }
  }

  const availableToAdd = getAvailableFeaturesToAdd()

  return (
    <div className='space-y-4'>
      {/* Header */}
      <div className='pb-3 border-b border-gray-200'>
        <h3 className='text-sm font-semibold text-gray-900'>Features</h3>
        <p className='text-xs text-gray-500 mt-0.5'>
          {features.length} feature{features.length !== 1 ? 's' : ''} configured
        </p>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className='flex items-center justify-center py-8'>
          <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-avi-primary'></div>
        </div>
      ) : (
        <div className='space-y-3'>
          {/* Features List */}
          {features.length > 0 ? (
            <div className='space-y-2'>
              {features.map((feature) => {
                // API spec uses featureId as UUID string
                const featureId = feature.featureId || feature.id
                // Use featureDisplayName for UI display
                const featureName =
                  feature.featureDisplayName || 'Unknown Feature'
                const isEnabled = feature.isEnabled
                const isSaving = savingFeatureId === featureId

                return (
                  <Card
                    key={featureId}
                    variant='outlined'
                    className={`!p-4 transition-all ${
                      isSaving ? 'opacity-60' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className='flex items-center justify-between gap-3'>
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2'>
                          <p className='text-sm font-medium text-gray-900 truncate'>
                            {featureName}
                          </p>
                          {isSaving && (
                            <div className='animate-spin rounded-full h-3 w-3 border-2 border-gray-300 border-t-avi-primary' />
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() =>
                          handleToggleFeatureClick(featureId, isEnabled)
                        }
                        disabled={isSaving}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          isEnabled ? 'bg-green-600' : 'bg-gray-300'
                        } ${
                          isSaving
                            ? 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer'
                        } focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-avi-primary`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            isEnabled ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </Card>
                )
              })}
            </div>
          ) : null}

          {/* Add New Feature Section - Minimalist Bottom */}
          {availableToAdd.length > 0 && !isAddingNew && (
            <button
              onClick={handleAddFeatureClick}
              className='w-full py-2.5 px-3 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-dashed border-gray-300 hover:border-gray-400 rounded-md transition-colors flex items-center justify-center gap-1.5'
            >
              <Plus className='h-3.5 w-3.5' />
              Add feature
            </button>
          )}

          {/* Add Feature Form - Expanded State */}
          {isAddingNew && (
            <div className='border border-gray-300 rounded-md p-3 bg-white'>
              <div className='space-y-2.5'>
                <select
                  value={selectedFeatureId || ''}
                  onChange={(e) => setSelectedFeatureId(e.target.value)}
                  className='w-full px-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-avi-primary focus:border-transparent'
                  disabled={availableToAdd.length === 0}
                  autoFocus
                >
                  <option value=''>Select a feature to add...</option>
                  {availableToAdd.map((feature) => (
                    <option key={feature.id} value={feature.id}>
                      {feature.displayName}
                    </option>
                  ))}
                </select>
                <div className='flex gap-2'>
                  <button
                    onClick={handleAddFeature}
                    disabled={!selectedFeatureId || savingFeatureId !== null}
                    className='flex-1 px-3 py-1.5 text-xs font-medium text-white bg-avi-primary hover:bg-avi-primary-dark rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    {savingFeatureId ? 'Adding...' : 'Add'}
                  </button>
                  <button
                    onClick={handleCancelAdd}
                    disabled={savingFeatureId !== null}
                    className='px-3 py-1.5 text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-50 transition-colors'
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Info Messages */}
          {features.length === 0 && availableToAdd.length === 0 && (
            <div className='text-center py-8'>
              <p className='text-xs text-gray-500'>No features available</p>
            </div>
          )}

          {availableToAdd.length === 0 && features.length > 0 && (
            <p className='text-xs text-gray-500 text-center py-2'>
              All available features have been added
            </p>
          )}
        </div>
      )}

      {/* Change Reason Modal */}
      <Modal
        isOpen={showReasonModal}
        onClose={handleCancelReasonModal}
        title={`${
          featureToToggle?.currentStatus ? 'Disable' : 'Enable'
        } Feature`}
      >
        <div className='space-y-3'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1.5'>
              Reason for change
            </label>
            <textarea
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder='Explain why you are making this change...'
              rows={3}
              className='w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent'
              autoFocus
            />
          </div>
          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={handleCancelReasonModal}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmToggle}
              disabled={!changeReason.trim()}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default OrganizationFeatures
